import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaCreditCard, FaEuroSign, FaFileInvoice, FaCheckCircle, FaUser, FaPaypal } from 'react-icons/fa';
import './Paiement.css';
import { displayMessage } from '../utils/i18nDisplay';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const MOTIF_OPTIONS = [
  { value: 'consultation', key: 'consultation' },
  { value: 'dossier-visa', key: 'visa' },
  { value: 'dossier-residence', key: 'residence' },
  { value: 'nationalite', key: 'nationalite' },
  { value: 'diplome', key: 'diplome' },
  { value: 'autre', key: 'autre' }
];

const Paiement = () => {
  const { t, i18n } = useTranslation();
  const langKey = i18n.resolvedLanguage || i18n.language;

  useEffect(() => {
    const onLanguageChanged = () => {
      setErrors({});
      setPaymentError('');
    };
    i18n.on('languageChanged', onLanguageChanged);
    return () => i18n.off('languageChanged', onLanguageChanged);
  }, [i18n]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [formData, setFormData] = useState({
    typePaiement: '',
    montant: '',
    nom: '',
    prenom: '',
    email: '',
    telephone: '',
    motif: '',
    reference: '',
    numeroCarte: '',
    nomCarte: '',
    dateExpiration: '',
    cvv: ''
  });

  const [errors, setErrors] = useState({});
  const [paymentStatus, setPaymentStatus] = useState(null); // 'success' | 'cancel' | null
  const [paymentError, setPaymentError] = useState('');
  const [sending, setSending] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('stripe'); // 'stripe' | 'paypal'
  const paypalCaptureDone = useRef(false);
  const stripeInvoiceSent = useRef(false);

  useEffect(() => {
    const success = searchParams.get('success');
    const cancel = searchParams.get('cancel');
    const paypal = searchParams.get('paypal');
    const token = searchParams.get('token');
    const sessionId = searchParams.get('session_id');
    if (cancel === '1') {
      setPaymentStatus('cancel');
      return;
    }
    if (success === '1' && paypal === '1' && token && !paypalCaptureDone.current) {
      paypalCaptureDone.current = true;
      (async () => {
        try {
          const res = await fetch(`${API_BASE}/api/capture-paypal-order`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ orderId: token })
          });
          const data = await res.json().catch(() => ({}));
          if (res.ok && data.success) {
            setSearchParams({ success: '1' });
            setPaymentStatus('success');
          } else {
            setPaymentError('paiementPage.errors.paypalConfirm');
          }
        } catch (err) {
          setPaymentError('paiementPage.errors.paypalUnable');
        }
      })();
      return;
    }
    if (success === '1' && sessionId && !stripeInvoiceSent.current) {
      stripeInvoiceSent.current = true;
      fetch(`${API_BASE}/api/send-payment-invoice`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId })
      }).catch(() => {});
    }
    if (success === '1') setPaymentStatus('success');
  }, [searchParams, setSearchParams]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.typePaiement) newErrors.typePaiement = 'paiementPage.errors.typePaiement';
    if (!formData.montant || parseFloat(formData.montant) <= 0) {
      newErrors.montant = 'paiementPage.errors.montant';
    }
    if (!formData.nom.trim()) newErrors.nom = 'paiementPage.errors.nom';
    if (!formData.prenom.trim()) newErrors.prenom = 'paiementPage.errors.prenom';
    if (!formData.email.trim()) {
      newErrors.email = 'paiementPage.errors.emailRequired';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'paiementPage.errors.emailInvalid';
    }
    if (!formData.telephone.trim()) newErrors.telephone = 'paiementPage.errors.telephone';
    if (!formData.motif) newErrors.motif = 'paiementPage.errors.motif';
    if (parseFloat(formData.montant) < 0.5) {
      newErrors.montant = 'paiementPage.errors.montantMin';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPaymentError('');
    if (!validate()) return;

    setSending(true);
    const payload = {
      amount: formData.montant,
      currency: 'eur',
      email: formData.email,
      typePaiement: formData.typePaiement,
      motif: formData.motif,
      nom: formData.nom,
      prenom: formData.prenom
    };
    const endpoint = paymentMethod === 'paypal'
      ? `${API_BASE}/api/create-paypal-order`
      : `${API_BASE}/api/create-checkout-session`;
    try {
      const res = await fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setPaymentError('paiementPage.errors.session');
        setSending(false);
        return;
      }
      if (data.url) {
        window.location.href = data.url;
        return;
      }
      setPaymentError('paiementPage.errors.invalidResponse');
    } catch (err) {
      setPaymentError('paiementPage.errors.server');
    }
    setSending(false);
  };

  if (paymentStatus === 'success') {
    const sessionId = searchParams.get('session_id');
    return (
      <div className="paiement-page" key={langKey}>
        <section className="page-hero">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center">
                <div className="success-message">
                  <FaCheckCircle className="success-icon" />
                  <h1 className="page-title">{t('paiementPage.success.title')}</h1>
                  <p className="page-subtitle">
                    {t('paiementPage.success.message')}
                    {sessionId && <><br /><small>{t('common.session', { id: sessionId })}</small></>}
                  </p>
                  <p className="page-subtitle">{t('paiementPage.success.confirmation')}</p>
                  <button
                    className="btn btn-hero"
                    onClick={() => { setSearchParams({}); setPaymentStatus(null); }}
                  >
                    {t('paiementPage.success.newPayment')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  if (paymentStatus === 'cancel') {
    return (
      <div className="paiement-page" key={langKey}>
        <section className="page-hero">
          <div className="container">
            <div className="row">
              <div className="col-lg-8 mx-auto text-center">
                <div className="success-message">
                  <h1 className="page-title">{t('paiementPage.cancel.title')}</h1>
                  <p className="page-subtitle">{t('paiementPage.cancel.message')}</p>
                  <button
                    className="btn btn-hero"
                    onClick={() => { setSearchParams({}); setPaymentStatus(null); }}
                  >
                    {t('paiementPage.cancel.retry')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="paiement-page" key={langKey}>
      <section className="page-hero">
        <div className="container">
          <div className="row align-items-center">
            <div className="col-lg-3 text-center">
              <img src="/images/logo.jpeg" alt={t('common.altLogo')} className="hero-logo" />
            </div>
            <div className="col-lg-9 text-center">
              <h1 className="page-title">{t('paiementPage.hero.title')}</h1>
              <p className="page-subtitle">{t('paiementPage.hero.subtitle')}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="form-section py-5">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-10">
              <form onSubmit={handleSubmit} className="paiement-form" noValidate>
                {/* Section Type de paiement */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaCreditCard className="section-icon" />
                    {t('paiementPage.sections.type')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div 
                        className={`payment-type-card ${formData.typePaiement === 'avance' ? 'selected' : ''}`}
                        onClick={() => setFormData({ ...formData, typePaiement: 'avance' })}
                      >
                        <FaEuroSign className="payment-icon" />
                        <h4>{t('paiementPage.paymentTypes.avance.title')}</h4>
                        <p>{t('paiementPage.paymentTypes.avance.description')}</p>
                        <input
                          type="radio"
                          name="typePaiement"
                          value="avance"
                          checked={formData.typePaiement === 'avance'}
                          onChange={handleChange}
                          className="d-none"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div 
                        className={`payment-type-card ${formData.typePaiement === 'frais' ? 'selected' : ''}`}
                        onClick={() => setFormData({ ...formData, typePaiement: 'frais' })}
                      >
                        <FaFileInvoice className="payment-icon" />
                        <h4>{t('paiementPage.paymentTypes.frais.title')}</h4>
                        <p>{t('paiementPage.paymentTypes.frais.description')}</p>
                        <input
                          type="radio"
                          name="typePaiement"
                          value="frais"
                          checked={formData.typePaiement === 'frais'}
                          onChange={handleChange}
                          className="d-none"
                        />
                      </div>
                    </div>
                    {errors.typePaiement && <div className="col-12"><div className="invalid-feedback d-block">{t(errors.typePaiement)}</div></div>}
                  </div>
                </div>

                {/* Section Montant */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaEuroSign className="section-icon" />
                    {t('paiementPage.sections.amount')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">{t('paiementPage.labels.montant')}</label>
                      <div className="input-group">
                        <span className="input-group-text">€</span>
                        <input
                          type="number"
                          className={`form-control ${errors.montant ? 'is-invalid' : ''}`}
                          name="montant"
                          value={formData.montant}
                          onChange={handleChange}
                          min="1"
                          step="0.01"
                          placeholder="0.00"
                          required
                        />
                        {errors.montant && <div className="invalid-feedback">{t(errors.montant)}</div>}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">{t('paiementPage.labels.motif')}</label>
                      <select
                        className={`form-select ${errors.motif ? 'is-invalid' : ''}`}
                        name="motif"
                        value={formData.motif}
                        onChange={handleChange}
                        required
                      >
                        <option value="">{t('paiementPage.motives.placeholder')}</option>
                        {MOTIF_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {t(`paiementPage.motives.${opt.key}`)}
                          </option>
                        ))}
                      </select>
                      {errors.motif && <div className="invalid-feedback">{t(errors.motif)}</div>}
                    </div>
                  </div>
                </div>

                {/* Section Informations personnelles */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaUser className="section-icon" />
                    {t('paiementPage.sections.personal')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">{t('paiementPage.labels.nom')}</label>
                      <input
                        type="text"
                        className={`form-control ${errors.nom ? 'is-invalid' : ''}`}
                        name="nom"
                        value={formData.nom}
                        onChange={handleChange}
                        required
                      />
                      {errors.nom && <div className="invalid-feedback">{t(errors.nom)}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">{t('paiementPage.labels.prenom')}</label>
                      <input
                        type="text"
                        className={`form-control ${errors.prenom ? 'is-invalid' : ''}`}
                        name="prenom"
                        value={formData.prenom}
                        onChange={handleChange}
                        required
                      />
                      {errors.prenom && <div className="invalid-feedback">{t(errors.prenom)}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">{t('paiementPage.labels.email')}</label>
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                      />
                      {errors.email && <div className="invalid-feedback">{t(errors.email)}</div>}
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">{t('paiementPage.labels.telephone')}</label>
                      <input
                        type="tel"
                        className={`form-control ${errors.telephone ? 'is-invalid' : ''}`}
                        name="telephone"
                        value={formData.telephone}
                        onChange={handleChange}
                        required
                      />
                      {errors.telephone && <div className="invalid-feedback">{t(errors.telephone)}</div>}
                    </div>
                  </div>
                </div>

                {/* Section Méthodes de paiement */}
                <div className="form-section-card">
                  <h2 className="form-section-title">
                    <FaCreditCard className="section-icon" />
                    {t('paiementPage.sections.method')}
                  </h2>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <div
                        className={`payment-method-card ${paymentMethod === 'stripe' ? 'selected' : ''}`}
                        onClick={() => setPaymentMethod('stripe')}
                        style={{ cursor: 'pointer' }}
                      >
                        <FaCreditCard className="method-icon" />
                        <h5>{t('paiementPage.methods.stripe.title')}</h5>
                        <p>{t('paiementPage.methods.stripe.description')}</p>
                        {paymentMethod === 'stripe' && <span className="method-badge">{t('paiementPage.methods.stripe.selected')}</span>}
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div
                        className={`payment-method-card ${paymentMethod === 'paypal' ? 'selected' : ''}`}
                        onClick={() => setPaymentMethod('paypal')}
                        style={{ cursor: 'pointer' }}
                      >
                        <FaPaypal className="method-icon" style={{ color: '#003087' }} />
                        <h5>{t('paiementPage.methods.paypal.title')}</h5>
                        <p>{t('paiementPage.methods.paypal.description')}</p>
                        {paymentMethod === 'paypal' && <span className="method-badge">{t('paiementPage.methods.paypal.selected')}</span>}
                      </div>
                    </div>
                  </div>


                  <div className="payment-note">
                    <p>
                      <strong>{t('paiementPage.notes.label')}</strong>{' '}
                      {t('paiementPage.notes.text', {
                        destination: paymentMethod === 'paypal'
                          ? t('paiementPage.notes.paypal')
                          : t('paiementPage.notes.stripe')
                      })}
                    </p>
                  </div>
                </div>

                {paymentError && (
                  <div className="alert alert-danger" role="alert">
                    {displayMessage(t, paymentError)}
                  </div>
                )}
                {/* Bouton de soumission */}
                <div className="form-submit-section">
                  <button type="submit" className="btn btn-submit" disabled={sending}>
                    {sending
                      ? t('paiementPage.buttons.redirecting')
                      : paymentMethod === 'paypal'
                        ? t('paiementPage.buttons.paypal')
                        : t('paiementPage.buttons.stripe')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Paiement;
